import { useEffect, useState } from "react";
import { Check, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const STEPS = ["Studio", "Staff & installers", "Opening stock", "Targets"];

const blankStaff = () => [
  { role: "studio_manager", label: "Studio Manager", name: "", mobile: "" },
  { role: "studio_owner", label: "Studio Owner", name: "", mobile: "" },
  { role: "content_manager", label: "Content Manager", name: "", mobile: "" },
];

export const StudioWizard = ({ regions = [], onDone }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [studio, setStudio] = useState(null);
  const [form, setForm] = useState({ name: "", city: "", region: "", address: "", phone: "" });
  const [staff, setStaff] = useState(blankStaff());
  const [teams, setTeams] = useState([{ name: "", leader_name: "", leader_mobile: "" }]);
  const [catalog, setCatalog] = useState([]);
  const [stock, setStock] = useState({});
  const [services, setServices] = useState([]);
  const [targets, setTargets] = useState({ revenue: "", units: {} });
  const [created, setCreated] = useState({ pins: [], stock: 0, teams: 0 });

  useEffect(() => {
    if (!open) return;
    api.get("/catalog").then(({ data }) => setCatalog(data));
    api.get("/config/services").then(({ data }) => setServices(data));
  }, [open]);

  const reset = () => {
    setStep(0);
    setStudio(null);
    setForm({ name: "", city: "", region: "", address: "", phone: "" });
    setStaff(blankStaff());
    setTeams([{ name: "", leader_name: "", leader_mobile: "" }]);
    setStock({});
    setTargets({ revenue: "", units: {} });
    setCreated({ pins: [], stock: 0, teams: 0 });
  };

  const createStudio = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studios", form);
      setStudio(data);
      setStep(1);
      toast.success(`${data.name} created — now add the team`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveStaff = async () => {
    setBusy(true);
    const pins = [...created.pins];
    let teamCount = created.teams;
    try {
      for (const s of staff) {
        if (!s.name || !s.mobile) continue;
        const { data } = await api.post("/users", {
          name: s.name,
          role: s.role,
          mobile: s.mobile,
          studio_id: studio.id,
        });
        pins.push({ name: data.name, role: s.label, mobile: data.mobile, pin: data.pin });
      }
      for (const t of teams) {
        if (!t.name || !t.leader_mobile) continue;
        await api.post("/teams", {
          name: t.name,
          leader_name: t.leader_name || t.name,
          leader_mobile: t.leader_mobile,
          studio_id: studio.id,
          installer_type: "studio",
          skills: [],
        });
        teamCount += 1;
      }
      setCreated((c) => ({ ...c, pins, teams: teamCount }));
      setStaff(blankStaff());
      setTeams([{ name: "", leader_name: "", leader_mobile: "" }]);
      setStep(2);
    } catch (e) {
      setCreated((c) => ({ ...c, pins, teams: teamCount }));
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveStock = async () => {
    setBusy(true);
    let count = created.stock;
    try {
      for (const [skuId, row] of Object.entries(stock)) {
        if (!Number(row.quantity) && !Number(row.threshold)) continue;
        await api.post("/stock", {
          catalog_sku_id: skuId,
          quantity: Number(row.quantity || 0),
          unit_cost: null,
          low_threshold: Number(row.threshold || 0),
          studio_id: studio.id,
        });
        count += 1;
      }
      setCreated((c) => ({ ...c, stock: count }));
      setStock({});
      setStep(3);
    } catch (e) {
      setCreated((c) => ({ ...c, stock: count }));
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/targets", {
        studio_id: studio.id,
        revenue_target: Number(targets.revenue || 0),
        service_targets: Object.fromEntries(
          Object.entries(targets.units)
            .filter(([, v]) => Number(v) > 0)
            .map(([k, v]) => [k, Number(v)]),
        ),
      });
      toast.success(`${studio.name} is live`);
      setStep(4);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    reset();
    onDone?.();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          reset();
          onDone?.();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="studio-wizard-btn">
          <Wand2 className="mr-2 h-4 w-4" /> Onboarding wizard
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
        data-testid="studio-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {step === 4 ? "Studio is live" : `New studio — step ${step + 1} of 4`}
          </DialogTitle>
        </DialogHeader>

        {step < 4 && (
          <div className="flex flex-wrap gap-2">
            {STEPS.map((s, i) => (
              <Badge
                key={s}
                data-testid={`wizard-step-${i}`}
                className={`border-0 ${
                  i === step
                    ? "bg-brand text-white"
                    : i < step
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                }`}
              >
                {i < step && <Check className="mr-1 h-3 w-3" />}
                {s}
              </Badge>
            ))}
          </div>
        )}

        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["name", "Studio name *"],
              ["city", "City *"],
              ["region", `Region (${regions.map((r) => r.name).join(", ")})`],
              ["address", "Address"],
              ["phone", "Phone"],
            ].map(([k, label]) => (
              <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                <Label>{label}</Label>
                <Input
                  className="min-h-[44px]"
                  data-testid={`wizard-studio-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <p className="text-sm text-slate-500">
              Each staff member signs in with their mobile and an auto-generated PIN, shown at the
              end. Leave a row blank to skip it.
            </p>
            {staff.map((s, i) => (
              <div key={s.role} className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>{s.label} name</Label>
                  <Input
                    className="min-h-[44px]"
                    data-testid={`wizard-staff-name-${s.role}`}
                    value={s.name}
                    onChange={(e) =>
                      setStaff((rows) =>
                        rows.map((r, j) => (i === j ? { ...r, name: e.target.value } : r)),
                      )
                    }
                  />
                </div>
                <div>
                  <Label>Mobile (10 digits)</Label>
                  <Input
                    className="min-h-[44px]"
                    inputMode="numeric"
                    data-testid={`wizard-staff-mobile-${s.role}`}
                    value={s.mobile}
                    onChange={(e) =>
                      setStaff((rows) =>
                        rows.map((r, j) => (i === j ? { ...r, mobile: e.target.value } : r)),
                      )
                    }
                  />
                </div>
              </div>
            ))}
            <div className="border-t border-brand-line pt-4">
              <p className="text-sm font-semibold text-brand-ink">Installer teams</p>
              {teams.map((t, i) => (
                <div key={i} className="mt-3 grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label>Team name</Label>
                    <Input
                      className="min-h-[44px]"
                      data-testid={`wizard-team-name-${i}`}
                      value={t.name}
                      onChange={(e) =>
                        setTeams((rows) =>
                          rows.map((r, j) => (i === j ? { ...r, name: e.target.value } : r)),
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label>Leader name</Label>
                    <Input
                      className="min-h-[44px]"
                      data-testid={`wizard-team-leader-${i}`}
                      value={t.leader_name}
                      onChange={(e) =>
                        setTeams((rows) =>
                          rows.map((r, j) => (i === j ? { ...r, leader_name: e.target.value } : r)),
                        )
                      }
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label>Leader mobile</Label>
                      <Input
                        className="min-h-[44px]"
                        inputMode="numeric"
                        data-testid={`wizard-team-mobile-${i}`}
                        value={t.leader_mobile}
                        onChange={(e) =>
                          setTeams((rows) =>
                            rows.map((r, j) =>
                              i === j ? { ...r, leader_mobile: e.target.value } : r,
                            ),
                          )
                        }
                      />
                    </div>
                    {teams.length > 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        data-testid={`wizard-team-remove-${i}`}
                        onClick={() => setTeams((rows) => rows.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                className="mt-3"
                data-testid="wizard-team-add"
                onClick={() =>
                  setTeams((rows) => [...rows, { name: "", leader_name: "", leader_mobile: "" }])
                }
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add another team
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Set the opening quantity and reorder level for the SKUs this studio holds. Leave the
              rest at zero — you can order from HQ later.
            </p>
            <div className="max-h-[45vh] space-y-2 overflow-y-auto">
              {catalog.map((c) => (
                <div key={c.id} className="grid grid-cols-3 items-end gap-3 rounded border border-brand-line p-3">
                  <div>
                    <p className="text-sm font-medium text-brand-ink">{c.name}</p>
                    <p className="text-xs text-slate-500">
                      {c.category} · {c.unit} · {money(c.hq_sale_price)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Opening qty</Label>
                    <Input
                      className="min-h-[40px]"
                      inputMode="decimal"
                      data-testid={`wizard-stock-qty-${c.id}`}
                      value={stock[c.id]?.quantity ?? ""}
                      onChange={(e) =>
                        setStock((s) => ({
                          ...s,
                          [c.id]: { ...(s[c.id] || {}), quantity: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Reorder at</Label>
                    <Input
                      className="min-h-[40px]"
                      inputMode="decimal"
                      data-testid={`wizard-stock-threshold-${c.id}`}
                      value={stock[c.id]?.threshold ?? ""}
                      onChange={(e) =>
                        setStock((s) => ({
                          ...s,
                          [c.id]: { ...(s[c.id] || {}), threshold: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <Label>Monthly revenue target (₹)</Label>
              <Input
                className="min-h-[44px]"
                inputMode="decimal"
                data-testid="wizard-revenue-target"
                value={targets.revenue}
                onChange={(e) => setTargets((t) => ({ ...t, revenue: e.target.value }))}
              />
            </div>
            <p className="text-sm font-semibold text-brand-ink">Unit targets per service</p>
            <div className="grid max-h-[40vh] gap-3 overflow-y-auto sm:grid-cols-2">
              {services.map((s) => (
                <div key={s.id}>
                  <Label className="text-xs">{s.name}</Label>
                  <Input
                    className="min-h-[40px]"
                    inputMode="numeric"
                    data-testid={`wizard-target-${s.id}`}
                    value={targets.units[s.name] ?? ""}
                    onChange={(e) =>
                      setTargets((t) => ({
                        ...t,
                        units: { ...t.units, [s.name]: e.target.value },
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-summary">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-brand-ink">{studio?.name}</span> is set up with{" "}
              {created.pins.length} staff login(s), {created.teams} installer team(s) and{" "}
              {created.stock} stocked SKU(s).
            </p>
            {created.pins.length > 0 && (
              <div className="rounded-md border border-brand-line">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="p-2">Name</th>
                      <th className="p-2">Role</th>
                      <th className="p-2">Mobile</th>
                      <th className="p-2">PIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {created.pins.map((p) => (
                      <tr key={p.mobile} className="border-b border-slate-100">
                        <td className="p-2 font-medium">{p.name}</td>
                        <td className="p-2 text-slate-500">{p.role}</td>
                        <td className="p-2 tabular-nums">{p.mobile}</td>
                        <td className="p-2 font-semibold tabular-nums">{p.pin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-slate-500">
              Share these PINs with the team — they are also visible on the Users screen and can be
              rotated there.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 0 && (
            <Button
              className="min-h-[44px]"
              data-testid="wizard-create-studio"
              disabled={busy || !form.name || !form.city}
              onClick={createStudio}
            >
              Create studio & continue
            </Button>
          )}
          {step === 1 && (
            <Button className="min-h-[44px]" data-testid="wizard-save-staff" disabled={busy} onClick={saveStaff}>
              Save team & continue
            </Button>
          )}
          {step === 2 && (
            <Button className="min-h-[44px]" data-testid="wizard-save-stock" disabled={busy} onClick={saveStock}>
              Save stock & continue
            </Button>
          )}
          {step === 3 && (
            <Button className="min-h-[44px]" data-testid="wizard-finish" disabled={busy} onClick={finish}>
              Set targets & go live
            </Button>
          )}
          {step === 4 && (
            <Button className="min-h-[44px]" data-testid="wizard-close" onClick={close}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
